/**
 * Lightweight product analytics.
 *
 * Logs key funnel events to console and persists a queue in localStorage.
 * In production this can be swapped for PostHog / Mixpanel / Plausible.
 */

export type AnalyticsEvent =
  | "landing_cast_chart"
  | "landing_reset_chart"
  | "landing_cta_click"
  | "cast_form_submit"
  | "reading_view_switch"
  | "explorer_topic_click"
  | "narrative_generated"
  | "chart_saved"
  | "deep_feature_entry"
  | "celebrity_demo_click"
  | "case_library_open"
  | "transit_toggle"
  | "council_entry"
  | "radio_entry"
  | "save_prompt_shown"
  | "save_prompt_dismissed"
  | "rectify_from_cast"
  | "watch_chapter_entered"
  | "movement_period_validated"
  | "newsletter_subscribe"
  | "mundane_tab_switch"
  | "chapter_view"            // session-2026-07-01 — chapter pages
  | "event_timing_compute"
  | "chart_shared"
  | "chart_exported"
  | "celebrity_similarity_match"
  | "celebrity_assessment_view"
  | "celebrity_assessment_deep_dive"
  | "see_my_chart_fallback"
  // Task 2 — homepage conversion events. Reuses the existing track() →
  // localStorage queue; no new analytics vendor.
  | "homepage_cast_click"      // hero primary CTA → /cast
  | "homepage_demo_select"
  | "i18n_fallback"
  // Task 4 — privacy-safe first-use activation events. See FORBIDDEN_KEYS
  // in track() below for the privacy contract. These events fire only
  // with allowlisted meta keys (entry_surface, personal_vs_demo,
  // canonical_chapter, coarse_duration_bucket, success_failure_category,
  // known_vs_unknown_time_mode).
  | "activation_home_cast_selected"
  | "activation_demo_selected"
  | "activation_cast_started"
  | "activation_location_resolved"
  | "activation_cast_submitted"
  | "activation_cast_succeeded"
  | "activation_cast_failed"
  | "activation_reading_ready"
  | "activation_story_opened"
  | "activation_chapter_opened"
  | "activation_today_opened"
  | "activation_continue_returned"
  | "activation_plans_viewed"
  // Phase 1+2 — Watch progression footer (chapter names only, no PII).
  | "watch_chapter_complete"
  | "watch_chapter_next"
  | "journey_progress_corrupt";   // internal: emitted when v2 record is unparseable

interface EventPayload {
  event: AnalyticsEvent;
  ts: number;
  path: string;
  meta?: Record<string, unknown>;
}

const QUEUE_KEY = "ag_analytics_queue";
const MAX_QUEUE = 500;

// ── Privacy guard for activation events (Task 4) ───────────────────────
//
// Every activation event MUST be scrubbed of any key that could leak a
// user's birth particulars, location, identity, or chart id. The
// allowlist below is the set of permitted meta keys. If a call passes
// a forbidden key (even accidentally), the event is dropped and a
// counter is incremented. The drop is silent at runtime (no console,
// no UI) — only the increment shows up in dev tooling.

const ACTIVATION_ALLOWED_KEYS = new Set([
  "entry_surface",
  "personal_vs_demo",
  "canonical_chapter",
  "duration_bucket",
  "reason",
  "success_failure_category",
  "mode",             // e.g. "known_time" | "rectify"
  "unsure_time",      // "yes" | "no"
  "from",             // navigation source (kept coarse; never includes chart id)
]);

const FORBIDDEN_KEYS_PATTERNS: RegExp[] = [
  /^birth/i,
  /^date$/i,
  /^time$/i,
  /^city$/i,
  /^lat/i,
  /^lon/i,
  /^lng/i,
  /^tz/i,
  /^timezone/i,
  /^name$/i,
  /^chart[_-]?id$/i,
  /^chartId$/i,
  /^slug$/i,
  /^payload$/i,
  /^response$/i,
  /^narrative$/i,
  /^story$/i,
  /^text$/i,
  /^url$/i,
  /^pathname$/i,
  /^full_url$/i,
  /^query$/i,
  /^body$/i,
  /^ts$/i,
  /^timestamp$/i,
  /^seconds$/i,
  /^minutes$/i,
  /^hours$/i,
  /^year$/i,
  /^month$/i,
];

function doNotTrackEnabled(): boolean {
  if (typeof navigator === "undefined") return false;
  // Standard + legacy IE/Edge flags.
  const dnt =
    (navigator as Navigator & { doNotTrack?: string }).doNotTrack ??
    (window as Window & { doNotTrack?: string }).doNotTrack ??
    (navigator as Navigator & { msDoNotTrack?: string }).msDoNotTrack;
  return dnt === "1" || dnt === "yes";
}

function scrubMeta(meta: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!meta) return meta;
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(meta)) {
    // Allow allowlist exact matches.
    if (ACTIVATION_ALLOWED_KEYS.has(k)) {
      // Reject nested objects (we only ship primitives in activation events).
      if (v !== null && typeof v === "object") {
        droppedForbiddenCount++;
        continue;
      }
      out[k] = v;
      continue;
    }
    // Reject anything matching a forbidden pattern.
    if (FORBIDDEN_KEYS_PATTERNS.some((re) => re.test(k))) {
      droppedForbiddenCount++;
      continue;
    }
    // Allowlist exact match for "reason" only — already handled above.
    // If the key isn't recognized, drop it. Activation events are NOT
    // a free-form dict; they're a closed schema.
    droppedForbiddenCount++;
  }
  return out;
}

let droppedForbiddenCount = 0;
/** Test-only: how many events have been dropped due to forbidden keys. */
export function _getDroppedForbiddenCount(): number {
  return droppedForbiddenCount;
}

function isActivationEvent(event: AnalyticsEvent): boolean {
  return event.startsWith("activation_") || event === "journey_progress_corrupt";
}

function now() {
  return Date.now();
}

function queue(): EventPayload[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function persist(events: EventPayload[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(events.slice(-MAX_QUEUE)));
  } catch {
    // Quota exceeded — silent drop
  }
}

export function track(event: AnalyticsEvent, meta?: Record<string, unknown>) {
  // Respect Do Not Track before doing anything else.
  if (doNotTrackEnabled()) return;

  let safeMeta: Record<string, unknown> | undefined = meta;
  if (isActivationEvent(event)) {
    safeMeta = scrubMeta(meta);
  }

  const payload: EventPayload = {
    event,
    ts: now(),
    path: window.location.pathname,
    meta: safeMeta,
  };

  // Dev-only logging — the prior unconditional console.log fired on every
  // track call (~27 sites) and cluttered production consoles. (Watch redesign
  // spec, Foundation-4.)
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log("[analytics]", event, safeMeta ?? "");
  }

  const q = queue();
  q.push(payload);
  persist(q);
}

/** Export queued events as a JSON blob (for later bulk upload). */
export function flushQueue(): EventPayload[] {
  const q = queue();
  try { localStorage.removeItem(QUEUE_KEY); } catch {}
  return q;
}
